import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { LearningHighlight } from '../../types/messaging';
import './LearningMomentCard.css';

interface LearningMomentCardProps {
    highlights: LearningHighlight[];
    onDismiss: () => void;
}

const LearningMomentCard: React.FC<LearningMomentCardProps> = ({ highlights, onDismiss }) => {
    const { t } = useTranslation('messaging');
    const [visibleCards, setVisibleCards] = useState<LearningHighlight[]>([]);
    const [collapsed, setCollapsed] = useState<LearningHighlight[]>([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (highlights.length === 0) {
            setVisibleCards([]);
            setCollapsed([]);
            setExpanded(false);
            return;
        }

        setVisibleCards(highlights.slice(0, 3));
        setCollapsed([]);
        setExpanded(false);

        // Auto-collapse after display time
        const timers = highlights.slice(0, 3).map((h, i) => {
            const delay = h.type === 'strength' ? 1500 : 3000;
            return setTimeout(() => {
                setVisibleCards(prev => prev.filter((_, idx) => idx !== 0));
                setCollapsed(prev => [...prev, h]);
            }, delay + i * 500);
        });

        return () => timers.forEach(clearTimeout);
    }, [highlights]);

    const handleDotClick = useCallback(() => {
        if (expanded) {
            setExpanded(false);
        } else {
            setVisibleCards(collapsed);
            setCollapsed([]);
            setExpanded(true);
            // Auto-dismiss after re-expand
            setTimeout(() => {
                setVisibleCards([]);
                setExpanded(false);
                onDismiss();
            }, 4000);
        }
    }, [collapsed, expanded, onDismiss]);

    if (visibleCards.length === 0 && collapsed.length === 0) return null;

    return (
        <div className="learning-moment-container">
            {/* Collapsed dots */}
            {collapsed.length > 0 && !expanded && (
                <div className="learning-moment-dots" onClick={handleDotClick}>
                    {collapsed.map((h, i) => (
                        <div
                            key={i}
                            className={`learning-moment-dot ${h.type === 'error' ? 'dot-error' : 'dot-strength'}`}
                        />
                    ))}
                </div>
            )}

            {/* Visible cards */}
            {visibleCards.map((highlight, index) => (
                <div
                    key={`${highlight.message}-${index}`}
                    className={`learning-moment-card ${highlight.type === 'error' ? 'card-error' : 'card-strength'}`}
                >
                    <div className="learning-moment-header">
                        {highlight.type === 'error' ? (
                            <svg className="learning-moment-icon icon-error" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        ) : (
                            <svg className="learning-moment-icon icon-strength" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                                <polyline points="22 4 12 14.01 9 11.01"/>
                            </svg>
                        )}
                        <span className="learning-moment-category">
                            {t(`chatAnalysis.${highlight.category}`)}
                        </span>
                        {highlight.cefrLevel && (
                            <span className="learning-moment-cefr">{highlight.cefrLevel}</span>
                        )}
                    </div>
                    <div className="learning-moment-body">
                        {highlight.found && highlight.correction ? (
                            <>
                                <span className="learning-moment-found">{highlight.found}</span>
                                <svg className="learning-moment-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <line x1="5" y1="12" x2="19" y2="12"/>
                                    <polyline points="12 5 19 12 12 19"/>
                                </svg>
                                <span className="learning-moment-correction">{highlight.correction}</span>
                            </>
                        ) : (
                            <span className="learning-moment-message">{highlight.message}</span>
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default LearningMomentCard;
